/**
 * @fileoverview Colector de métricas para el microservicio de sincronización
 * Integrado con sistema de autenticación OAuth2 y la robusta configuración de Firebase
 * Cumple principios SOLID y ofrece análisis de rendimiento y diagnóstico avanzado
 */

import syncMonitor from './SyncServiceMonitor';
import appConfig from '../infrastructure/config/AppConfig';
import TokenServiceFacade from '../infrastructure/auth/TokenServiceFacade';

// Constantes de configuración
const METRICS_ENDPOINT = '/metrics';
const PERFORMANCE_THRESHOLD_MS = 1500;
const COLLECTION_INTERVAL = 300000; // 5 minutos
const METRICS_RETENTION_DAYS = 30;

/**
 * Recolector de métricas para el microservicio de sincronización
 * Implementa patrón Strategy para múltiples estrategias de recolección
 */
class SyncMetricsCollector {
  constructor() {
    this.collectors = {
      performance: this._collectPerformanceMetrics.bind(this),
      availability: this._collectAvailabilityMetrics.bind(this),
      errors: this._collectErrorMetrics.bind(this),
      usage: this._collectUsageMetrics.bind(this)
    };
    
    this.collectionInterval = null;
    this.isCollecting = false;
    this.lastCollectionTime = null;
    this.metrics = {
      performance: [],
      availability: [],
      errors: [],
      usage: []
    };

    // Integración con monitor
    syncMonitor.onMetricsUpdate(this._handleMetricsUpdate.bind(this));
  }

  /**
   * Inicia la recolección periódica de métricas
   * @param {Object} options Opciones de configuración
   * @returns {SyncMetricsCollector} Instancia para encadenamiento
   */
  startCollection(options = {}) {
    if (this.isCollecting) return this;
    
    const interval = options.interval || COLLECTION_INTERVAL;
    
    // Recolectar inmediatamente
    this._collectAllMetrics();
    
    // Configurar recolección periódica
    this.collectionInterval = setInterval(() => {
      this._collectAllMetrics();
    }, interval);
    
    this.isCollecting = true;
    console.log(`[SyncMetrics] Recolección iniciada con intervalo de ${interval}ms`);
    
    return this;
  }

  /**
   * Detiene la recolección periódica de métricas
   * @returns {SyncMetricsCollector} Instancia para encadenamiento
   */
  stopCollection() {
    if (!this.isCollecting) return this;
    
    clearInterval(this.collectionInterval);
    this.collectionInterval = null;
    this.isCollecting = false;
    
    console.log('[SyncMetrics] Recolección detenida');
    return this;
  }

  /**
   * Obtiene métricas recolectadas
   * @param {string} type Tipo de métrica (opcional)
   * @returns {Object} Métricas recolectadas
   */
  getMetrics(type) {
    if (type && this.metrics[type]) {
      return this.metrics[type];
    }
    
    return this.metrics;
  }

  /**
   * Exporta métricas a formato CSV
   * @param {string} type Tipo de métrica
   * @returns {string} Datos en formato CSV
   */
  exportMetricsToCSV(type) {
    if (!type || !this.metrics[type]) {
      throw new Error(`Tipo de métrica inválido: ${type}`);
    }
    
    const metrics = this.metrics[type];
    if (!metrics.length) return '';
    
    // Obtener encabezados del primer objeto
    const headers = Object.keys(metrics[0]).filter(k => 
      k !== 'raw' && typeof metrics[0][k] !== 'object'
    );
    
    // Construir CSV
    const headerRow = headers.join(',');
    const rows = metrics.map(metric => 
      headers.map(h => typeof metric[h] === 'string' ? `"${metric[h]}"` : metric[h]).join(',')
    );
    
    return [headerRow, ...rows].join('\n');
  }

  /**
   * Envía métricas al backend para almacenamiento y análisis
   * Utiliza TokenServiceFacade para autenticación OAuth2
   * @returns {Promise<Object>} Resultado del envío
   */
  async submitMetrics() {
    try {
      // Usar TokenServiceFacade para obtener token OAuth2
      const tokenService = new TokenServiceFacade();
      const token = await tokenService.getToken();
      
      if (!token) {
        throw new Error('No se pudo obtener token OAuth2 para envío de métricas');
      }
      
      // Preparar datos
      const metricsData = {
        timestamp: Date.now(),
        serviceIdentifier: 'sync.nubelytics.com',
        environment: appConfig.environment || 'production',
        metrics: {
          performance: this._summarizeMetrics('performance'),
          availability: this._summarizeMetrics('availability'),
          errors: this._summarizeMetrics('errors', 10), // Solo últimos 10 errores
          usage: this._summarizeMetrics('usage')
        }
      };
      
      // Enviar al endpoint de métricas
      const metricsUrl = appConfig.getSyncUrl(METRICS_ENDPOINT);
      const response = await fetch(metricsUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Metrics-Source': 'sync-metrics-collector'
        },
        body: JSON.stringify(metricsData)
      });
      
      if (!response.ok) {
        throw new Error(`Error enviando métricas: ${response.status}`);
      }
      
      const result = await response.json();
      console.log(`[SyncMetrics] Métricas enviadas correctamente: ${result.storedEntries || 0} entradas`);
      
      // Limpiar métricas antiguas después de enviar
      this._cleanupOldMetrics();
      
      return result;
    } catch (error) {
      console.error('[SyncMetrics] Error enviando métricas:', error);
      
      // Guardar error para diagnóstico
      try {
        const errors = JSON.parse(localStorage.getItem('nubelytics_metrics_errors') || '[]');
        errors.push({
          timestamp: Date.now(),
          message: error.message,
          stack: error.stack
        });
        
        // Limitar tamaño
        if (errors.length > 20) errors.splice(0, errors.length - 20);
        
        localStorage.setItem('nubelytics_metrics_errors', JSON.stringify(errors));
      } catch (e) {/* Error no crítico */}
      
      // Reintento automático después de período de espera
      setTimeout(() => {
        if (this.isCollecting) {
          console.log('[SyncMetrics] Reintentando envío de métricas...');
          this.submitMetrics().catch(() => {});
        }
      }, 60000); // Reintento después de 1 minuto
      
      throw error;
    }
  }

  /**
   * Recolecta todas las métricas disponibles
   * @private
   */
  _collectAllMetrics() {
    // Registrar tiempo de recolección
    this.lastCollectionTime = Date.now();
    
    // Recolectar métricas de cada tipo
    Object.keys(this.collectors).forEach(type => {
      try {
        this.collectors[type]();
      } catch (error) {
        console.error(`[SyncMetrics] Error recolectando métricas de ${type}:`, error);
      }
    });
    
    // Enviar métricas al backend si hay suficientes
    const threshold = 5;
    const hasEnoughMetrics = Object.values(this.metrics).some(arr => arr.length >= threshold);
    
    if (hasEnoughMetrics) {
      this.submitMetrics().catch(error => {
        console.error('[SyncMetrics] Error en envío automático:', error);
      });
    }
  }

  /**
   * Maneja actualizaciones de métricas del monitor
   * @param {Object} metrics Métricas actualizadas
   * @private
   */
  _handleMetricsUpdate(metrics) {
    // No procesar cada actualización para evitar sobrecarga
    // Solo registrar métricas críticas inmediatamente
    if (metrics.isCritical) {
      this._collectErrorMetrics(metrics);
      
      // Enviar inmediatamente si es crítico
      this.submitMetrics().catch(() => {});
    }
  }

  /**
   * Recolecta métricas de rendimiento
   * @private
   */
  _collectPerformanceMetrics() {
    const monitorMetrics = syncMonitor.getMetrics();
    
    this.metrics.performance.push({
      timestamp: Date.now(),
      averageResponseTime: monitorMetrics.averageResponseTime,
      isAboveThreshold: monitorMetrics.averageResponseTime > PERFORMANCE_THRESHOLD_MS,
      threshold: PERFORMANCE_THRESHOLD_MS,
      requestCount: monitorMetrics.totalRequests
    });
    
    // Limitar número de entradas
    if (this.metrics.performance.length > 100) {
      this.metrics.performance.shift();
    }
  }

  /**
   * Recolecta métricas de disponibilidad
   * @private
   */
  _collectAvailabilityMetrics() {
    const monitorMetrics = syncMonitor.getMetrics();
    
    this.metrics.availability.push({
      timestamp: Date.now(),
      status: monitorMetrics.status,
      isHealthy: monitorMetrics.status === 'healthy',
      uptime: monitorMetrics.uptime
    });
    
    // Limitar número de entradas
    if (this.metrics.availability.length > 100) {
      this.metrics.availability.shift();
    }
  }

  /**
   * Recolecta métricas de errores
   * @param {Object} forcedMetrics Métricas forzadas (opcional)
   * @private
   */
  _collectErrorMetrics(forcedMetrics) {
    const monitorMetrics = forcedMetrics || syncMonitor.getMetrics();
    
    if (monitorMetrics.totalErrors > 0) {
      this.metrics.errors.push({
        timestamp: Date.now(),
        errorRate: monitorMetrics.errorRate,
        totalErrors: monitorMetrics.totalErrors,
        status: monitorMetrics.status,
        isCritical: monitorMetrics.isCritical
      });
      
      // Limitar número de entradas
      if (this.metrics.errors.length > 100) {
        this.metrics.errors.shift();
      }
    }
  }

  /**
   * Recolecta métricas de uso
   * @private
   */
  _collectUsageMetrics() {
    const monitorMetrics = syncMonitor.getMetrics();
    
    this.metrics.usage.push({
      timestamp: Date.now(),
      totalRequests: monitorMetrics.totalRequests,
      // Calcular tasa de solicitudes por minuto
      requestRate: this._calculateRequestRate(monitorMetrics.totalRequests)
    });
    
    // Limitar número de entradas
    if (this.metrics.usage.length > 100) {
      this.metrics.usage.shift();
    }
  }

  /**
   * Calcula tasa de solicitudes basada en historial
   * @param {number} currentTotal Total actual de solicitudes
   * @returns {number} Tasa de solicitudes por minuto
   * @private
   */
  _calculateRequestRate(currentTotal) {
    // Si no hay métrica anterior, devolver 0
    if (this.metrics.usage.length === 0) return 0;
    
    const lastUsageMetric = this.metrics.usage[this.metrics.usage.length - 1];
    const timeDiffMs = Date.now() - lastUsageMetric.timestamp;
    const timeDiffMinutes = timeDiffMs / 60000;
    
    // Prevenir división por cero
    if (timeDiffMinutes < 0.01) return 0;
    
    const requestDiff = currentTotal - lastUsageMetric.totalRequests;
    return requestDiff / timeDiffMinutes;
  }

  /**
   * Crea resumen de métricas para envío
   * @param {string} type Tipo de métricas
   * @param {number} limit Límite de entradas (opcional)
   * @returns {Array} Métricas resumidas
   * @private
   */
  _summarizeMetrics(type, limit) {
    if (!this.metrics[type]) return [];
    
    let metrics = [...this.metrics[type]];
    
    // Aplicar límite si se especifica
    if (limit && metrics.length > limit) {
      metrics = metrics.slice(-limit);
    }
    
    return metrics;
  }

  /**
   * Elimina métricas antiguas para liberar memoria
   * @private
   */
  _cleanupOldMetrics() {
    const cutoffTime = Date.now() - (METRICS_RETENTION_DAYS * 24 * 60 * 60 * 1000);
    
    // Limpiar cada tipo de métrica
    Object.keys(this.metrics).forEach(type => {
      this.metrics[type] = this.metrics[type].filter(
        metric => metric.timestamp >= cutoffTime
      );
    });
    
    console.log('[SyncMetrics] Limpieza de métricas antiguas completada');
  }
}

// Instancia única
const metricsCollector = new SyncMetricsCollector();

// Inicializar automáticamente si está en producción
if (appConfig.environment === 'production' || 
    (typeof window !== 'undefined' && window.location.hostname.includes('nubelytics.com'))) {
  metricsCollector.startCollection();
}

export default metricsCollector;
